import type { CSSProperties, DragEvent, KeyboardEvent } from "react";
import type { DropTarget, ReorderItemKind } from "../../hooks/use-reorder";
import { AddVectorControls } from "../AddControls";
import type { Equation } from "@src/features/equation-editor/equation";
import { DiagnosticField } from "./DiagnosticField";
import type { EquationViewModel } from "@src/features/equation-editor/lib/view-model";
import { MAX_EXPRESSION_SOURCE_LENGTH } from "@src/features/equation-editor/equation-policy";
import type { Translate } from "@src/i18n/translate";
import { VectorSymbol } from "../VectorSymbol";
import {
  calculateDragPreviewAnchor,
  createVectorDragPreview,
  initializeReorderDrag,
  isInteractiveDragTarget,
} from "../../lib/drag";
import { diagnosticMessage } from "../../lib/diagnostic-message";
import { getEquationViewModel } from "@src/features/equation-editor/lib/view-model";
import { renderEntryColumnTemplate } from "../../lib/equation-layout";
import { useInteractiveDragSuppression } from "../../hooks/use-interactive-drag-suppression";
import type { Dimension } from "@src/math/types";

export function InputVectorMatrix<D extends Dimension>({
  equation,
  viewModel = getEquationViewModel(equation),
  t,
  onAdd,
  onEigenbasis,
  onEigenvector,
  onDelete,
  onCoordinateChange,
  onKeyDown,
  onDragStart,
  draggingId,
  dropTarget,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  equation: Equation<D>;
  viewModel?: EquationViewModel;
  t: Translate;
  onAdd: () => void;
  onEigenbasis: () => void;
  onEigenvector: () => void;
  onDelete: (id: string) => void;
  onCoordinateChange: (id: string, index: number, value: string) => boolean;
  onKeyDown: (
    e: KeyboardEvent<HTMLElement>,
    kind: ReorderItemKind,
    id: string,
  ) => void;
  onDragStart: (id: string, preview: HTMLElement) => void;
  draggingId?: string | undefined;
  dropTarget: DropTarget | null;
  onDragOver: (e: DragEvent<HTMLElement>, id: string) => void;
  onDragEnd: () => void;
  onDrop: (
    e: DragEvent<HTMLElement>,
    kind: ReorderItemKind,
    id: string,
  ) => void;
}) {
  const { draggable, onPointerDownCapture, stopSuppressingDrag } =
    useInteractiveDragSuppression();
  const template = renderEntryColumnTemplate(
    equation.vectors.map((vector) => vector.coordinates),
    ["60px"],
  );
  return (
    <article
      className={`vector-matrix-card vector-matrix-card-${String(equation.dimension)}`}
      aria-label={t("inputVectorMatrix")}
    >
      <div
        className="vector-column-labels"
        style={{ gridTemplateColumns: template }}
      >
        {equation.vectors.map((vector) => {
          return (
            <div
              key={vector.id}
              className="vector-column-label"
              data-dragging={draggingId === vector.id || undefined}
              data-drop-position={
                dropTarget?.id === vector.id ? dropTarget.side : undefined
              }
              draggable={draggable}
              tabIndex={0}
              role="group"
              aria-label={vector.label}
              aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
              aria-describedby="reorder-instructions"
              onPointerDownCapture={onPointerDownCapture}
              onPointerUpCapture={stopSuppressingDrag}
              onPointerCancelCapture={stopSuppressingDrag}
              onLostPointerCapture={stopSuppressingDrag}
              style={{ "--vector": vector.color } as CSSProperties}
              onKeyDown={(event) => {
                onKeyDown(event, "vector", vector.id);
              }}
              onDragStart={(event) => {
                if (!draggable || isInteractiveDragTarget(event.target)) {
                  event.preventDefault();
                  return;
                }
                const bounds = event.currentTarget.getBoundingClientRect();
                const preview = createVectorDragPreview(
                  vector,
                  equation.dimension,
                );
                document.body.append(preview);
                initializeReorderDrag(event.dataTransfer);
                const previewHeader = preview.querySelector<HTMLElement>(
                  ".vector-column-label",
                );
                if (!previewHeader)
                  throw new Error("Missing vector drag preview header");
                const anchor = calculateDragPreviewAnchor(
                  event,
                  bounds,
                  preview.getBoundingClientRect(),
                  previewHeader.getBoundingClientRect(),
                );
                event.dataTransfer.setDragImage(preview, anchor.x, anchor.y);
                onDragStart(vector.id, preview);
              }}
              onDragOver={(event) => {
                onDragOver(event, vector.id);
              }}
              onDragEnd={onDragEnd}
              onDrop={(event) => {
                onDrop(event, "vector", vector.id);
              }}
            >
              <math className="vector-label" aria-label={vector.label}>
                <VectorSymbol label={vector.label} />
              </math>
              <span className="vector-color-label" aria-hidden="true" />
              <button
                className="equation-delete-button"
                type="button"
                onClick={() => {
                  onDelete(vector.id);
                }}
                aria-label={t("deleteVector", {
                  label: vector.label,
                })}
                title={t("deleteVector", {
                  label: vector.label,
                })}
              >
                ×
              </button>
            </div>
          );
        })}
        <AddVectorControls
          equation={equation}
          capabilities={viewModel.capabilities}
          t={t}
          onAdd={onAdd}
          onEigenbasis={onEigenbasis}
          onEigenvector={onEigenvector}
          inline
        />
      </div>
      <div
        className={`vector-expression vector-expression-${String(equation.dimension)}`}
        style={{ gridTemplateColumns: template }}
        role="group"
        aria-label={t("inputVectorColumns")}
      >
        {Array.from({ length: equation.dimension }, (_, component) => (
          <span key={component} style={{ display: "contents" }}>
            {equation.vectors.map((vector) => {
              const inputId = `vector-${vector.id}-component-${String(component)}`;
              const diagnostics = viewModel.diagnosticsFor(
                vector.id,
                "vector-coordinate",
                component,
              );
              const messages =
                diagnostics.length > 0
                  ? diagnostics.map((diagnostic) =>
                      diagnosticMessage(t, diagnostic.code),
                    )
                  : [];
              return (
                <DiagnosticField
                  key={vector.id}
                  id={inputId}
                  messages={messages}
                  indicatorLabel={t("inputErrors")}
                >
                  {(invalid, describedBy) => (
                    <input
                      id={inputId}
                      name={inputId}
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      maxLength={MAX_EXPRESSION_SOURCE_LENGTH}
                      value={vector.coordinates[component]}
                      aria-invalid={invalid || undefined}
                      aria-describedby={describedBy}
                      aria-label={t("vectorComponent", {
                        label: vector.label,
                        component: component + 1,
                      })}
                      onChange={(event) =>
                        onCoordinateChange(
                          vector.id,
                          component,
                          event.target.value,
                        )
                      }
                    />
                  )}
                </DiagnosticField>
              );
            })}
            <span className="vector-add-cell" aria-hidden="true" />
          </span>
        ))}
      </div>
      <div className="card-balance-row" aria-hidden="true" />
    </article>
  );
}
